import { Task } from "@coworker/apprestructured/src/task-management/types/Task";
import PendingTaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/variants/PendingTaskCard/PendingTaskCard";
import { timestampToTimeDiff } from "@coworker/apprestructured/src/task-management/util";
import CardFragment, {
  CardFragmentCommonProps,
} from "@coworker/apprestructured/src/task-management/components/TaskCard/card-fragments/CardFragment";
import { useMemo } from "react";
import CompletedTaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/variants/CompletedTaskCard/CompletedTaskCard";
import ClosedTaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/variants/ClosedTaskCard/ClosedTaskCard";
import {
  isLoading,
  LOADING,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingTaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/components/LoadingTaskCard/LoadingTaskCard";
import LoadingCardFragments from "@coworker/apprestructured/src/task-management/components/TaskCard/card-fragments/LoadingCardFragments/LoadingCardFragments";
import { useTranslation } from "@coworker/locales";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useAllCloseReasons } from "@coworker/apprestructured/src/task-management/services/useAllCloseReasons";
import { useStoreTeams } from "@coworker/app/src/core/hooks/useStoreTeams";
import { useAllStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useAllStoreUsers";
import { TaskType } from "@coworker/apprestructured/src/task-management/types/TaskType";
import Start from "@coworker/apprestructured/src/task-management/components/TaskCard/components/Start/Start";

type TaskCardProps = {
  task: Task | Loading;
};

/**
 * This is a convenience component so that it is easy to render a task card
 * without fetching all data separately.
 *
 * Apart from the accepted 'task' prop, this component loads of the data it needs.
 * These are very expensive backend calls and is a sane thing to do only because
 * these are all cached, meaning rendering many instances of this component will
 * only result in a couple of backend calls.
 */
function TaskCard({ task }: TaskCardProps) {
  const { t } = useTranslation();

  const { data: taskTypes } = useAllTaskTypes();
  const { data: closeReasons } = useAllCloseReasons();
  const teams = useStoreTeams();
  const { data: users } = useAllStoreUsers();

  const taskType = useMemo(() => {
    if (isLoading(task)) {
      return LOADING;
    } else {
      return (
        (taskTypes?.find(({ id }) => id === task.task_type_id) as TaskType) ??
        LOADING
      );
    }
  }, [task, taskTypes]);

  const assignedUser = useMemo(() => {
    if (isLoading(task)) {
      return LOADING;
    } else if (task.assigned_user_id === null) {
      return null;
    } else {
      return users?.find(({ uid }) => uid === task.assigned_user_id) ?? LOADING;
    }
  }, [task, users]);

  const assignedTeam = useMemo(() => {
    if (isLoading(task)) {
      return LOADING;
    } else {
      return teams.find(({ id }) => id === task.assigned_team_id) ?? LOADING;
    }
  }, [task, teams]);

  const closeReason = useMemo(() => {
    if (isLoading(task)) {
      return LOADING;
    } else if (task.close_reason_id === null) {
      return null;
    } else {
      return (
        closeReasons?.find(({ id }) => id === task.close_reason_id) ?? LOADING
      );
    }
  }, [closeReasons, task]);

  const cardFragmentCommonProps: CardFragmentCommonProps = useMemo(() => {
    return { task };
  }, [task]);

  const fragments = useMemo(() => {
    if (isLoading(taskType)) {
      return <LoadingCardFragments />;
    } else {
      return taskType.card_fragments.map((cardFragmentConfig, i) => {
        return (
          <CardFragment
            key={i}
            config={cardFragmentConfig}
            commonProps={cardFragmentCommonProps}
          />
        );
      });
    }
  }, [cardFragmentCommonProps, taskType]);

  const startElement = useMemo(() => {
    if (isLoading(task)) {
      return LOADING;
    } else if (task.start === null) {
      return null;
    } else {
      return <Start date={task.start} />;
    }
  }, [task]);

  if (isLoading(task)) {
    return <LoadingTaskCard />;
  }

  const taskTypeValue = isLoading(taskType) ? LOADING : t(taskType.label);

  const assignedValue =
    assignedUser === null
      ? isLoading(assignedTeam)
        ? LOADING
        : assignedTeam.name
      : isLoading(assignedUser)
        ? LOADING
        : assignedUser.fullName;

  switch (task.state) {
    case "pending": {
      return (
        <PendingTaskCard
          taskType={taskTypeValue}
          priority={task.priority}
          date={`${t("createdString")} ${timestampToTimeDiff({
            dateTime: task.created_at,
            t,
          })}`}
          assigned={assignedValue}
          startElement={startElement}
        >
          {fragments}
        </PendingTaskCard>
      );
    }
    case "completed": {
      return (
        <CompletedTaskCard
          taskType={taskTypeValue}
          priority={task.priority}
          date={`${t("completd3String")} ${timestampToTimeDiff({
            dateTime: task.completed_at,
            t,
          })}`}
          assigned={assignedValue}
        >
          {fragments}
        </CompletedTaskCard>
      );
    }
    case "closed": {
      const closeReasonValue = isLoading(closeReason)
        ? LOADING
        : t(closeReason!.label);
      return (
        <ClosedTaskCard
          taskType={taskTypeValue}
          priority={task.priority}
          date={`${t("closedFirstUppercaseString")} ${timestampToTimeDiff({
            dateTime: task.closed_at,
            t,
          })}`}
          assigned={assignedValue}
          closeReason={closeReasonValue}
        >
          {fragments}
        </ClosedTaskCard>
      );
    }
  }
}

export default TaskCard;
